import { EMPTY_ARRAY } from 'constants/defaults';
import { HIDE_TASK_SIDEBAR, PREFERRED_FORECASTS_VIEW_TYPE } from 'constants/user';
import type { ProjectDropdownOption } from 'factories/projects';
import { intersection } from 'lodash-es';
import { DateTime } from 'luxon';
import { getUserCapacitiesEntities } from 'redux/modules/capacities';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getAccountCurrency, getAccountFeatures, getStartOfWeek } from 'redux/selectors/accounts';
import {
  getFilter as getCalendarFilter,
  getCalendarUserId,
  getDates,
} from 'redux/selectors/calendar.filter';
import { getActiveClients } from 'redux/selectors/clients';
import {
  getAllHours,
  getHourProjectEntities,
  getHoursAsCards,
  getHoursXhrStatus,
} from 'redux/selectors/hours';
import {
  getAllLoadedProjects,
  getProjectPermissionsMap,
  getProjectVariantMap,
  getRecentlyUsedPinnedProjectIdsSet,
} from 'redux/selectors/projects';
import { getTeams } from 'redux/selectors/teams';
import { getUserId, getUserPermissions, getUserProperties } from 'redux/selectors/user';
import { getNonDeletedUsers, getUserEntities } from 'redux/selectors/users';
import {
  type Capacity,
  getNumberOfWorkingDaysForAUser,
  getRelevantCapacitiesForAUser,
} from 'redux/selectors/utils/capacities';
import {
  enrichProjectVariantOrNull,
  projectSelectorFilterProjectIds,
} from 'redux/selectors/utils/projects';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { enrichForecast } from 'routes/forecast/factories/forecast';
import type {
  ForecastStorageKeys,
  ForecastStorageStates,
} from 'routes/forecast/redux/modules/forecast.types';
import type {
  EnrichedForecast,
  EnrichedForecastDraft,
  Forecast,
  ForecastViewType,
} from 'routes/forecast/types/forecast';
import { FORECAST_VIEW_TYPES } from 'routes/forecast/types/forecast';
import type { ForecastsSummary } from 'routes/forecast/utils/forecast';
import {
  calculateForecastTotalsForAssignee,
  calculateForecastTotalsForProject,
  filterForecastListForUser,
  filterForecastsByQuery,
  generateAssigneeGroupsFromForecasts,
  getFinalForecastQueryUserIds,
  sortForecastList,
} from 'routes/forecast/utils/forecast';
import type { ProjectBase } from 'types/project';
import { getLastDayOfWeek, getNumberOfDaysInRange } from 'utils/date-helper';
import { fromISO } from 'utils/luxon-helpers';
import { getProjectColor } from 'utils/project';
import { groupBy, groupByKeys, notNull, orderBy, parseEnumValue, sortBy } from 'utils/typesafe';
import { UNASSIGNED_FORECAST_GROUP_KEY } from './forecast.constants';

export { UNASSIGNED_FORECAST_GROUP_KEY } from './forecast.constants';

const readXhrStatus = (state: IState) => state.forecast.xhrStatus;
const readEntities = (state: IState) => state.forecast.entities;
const readResult = (state: IState) => state.forecast.result;
const readForecastDraft = (state: IState) => state.forecast.draft;
const readForecastPagination = (state: IState) => state.forecast.pagination;
const readForecastQuery = (state: IState) => state.forecast.query;
const readForecastSelections = (state: IState) => state.forecast.selection;
const readDidSetPopoverPrefInSession = (state: IState) => state.forecast.didSetPopoverPrefInSession;

export const getDidSetPopoverPrefInSession = readDidSetPopoverPrefInSession;

export const getForecastSelections = readForecastSelections;

export const getForecastSelectionsAsSet = createSelector(
  [readForecastSelections],
  selections => new Set(selections.forecastIds)
);

export const getForecastPagination = readForecastPagination;

export const getRawXhrStatus = readXhrStatus;

export const getShowForecastEditor = createSelector([readForecastDraft], draft => !!draft);

export const getForecasts = createSelector(
  [readEntities, readResult, getAccountFeatures],
  (entities, result, features): Array<Forecast> => {
    const emptyForecasts: Array<Forecast> = [];
    if (!features.tasks) {
      return emptyForecasts;
    }
    return result.map(r => entities[r] || null).filter(notNull);
  }
);

export const getForecastXhrUiState = createSelector([getRawXhrStatus], xhrStatus =>
  xhrStatusUIState(xhrStatus)
);

export const getForecastLoadingState = createSelector(
  [getForecastXhrUiState],
  xhrStatus => xhrStatus.isLoading
);

export const getForecastQuery = readForecastQuery;

export const getForecastQueryUserIds = createSelector(
  [getForecastQuery, getTeams],
  (query, teams) => [...getFinalForecastQueryUserIds(query, teams)]
);

export const getForecastDraft = readForecastDraft;

export const getForecastEntities = readEntities;

export const getSortedForecasts = createSelector([getForecasts], forecasts => {
  return forecasts.sort(sortForecastList);
});

export const getPlainForecastsByAssignee = createSelector([getForecasts], forecasts =>
  groupByKeys(forecasts, f =>
    f.users.length < 1 ? [UNASSIGNED_FORECAST_GROUP_KEY] : f.users?.map(u => u.id)
  )
);
export const getPlainForecastsByProject = createSelector([getForecasts], forecasts =>
  groupBy(forecasts, f => f.project?.id)
);

/**
 * Enriched forecasts include .meta about the forecast and how it
 * relates to other forecasts/deadlines, offsets/timeline positions, etc.
 **/
export const getEnrichedForecasts = createSelector(
  [getSortedForecasts, getUserCapacitiesEntities],
  (forecasts, userCapacityEntities) => {
    return forecasts.map(f => enrichForecast(f, userCapacityEntities));
  }
);

// We want to avoid re-running getEnrichedForecasts every time we toggle a draft
// so we jump through some hoops in here to update the Draft in place and avoid doing that
export const getEnrichedForecastsWithDraft = createSelector(
  [getEnrichedForecasts, getForecastDraft, getUserCapacitiesEntities],
  (forecasts, draft, userCapacityEntities) => {
    const clonedForecasts = forecasts.slice();
    let enrichedDraft: EnrichedForecastDraft | null = null;
    if (draft) {
      // Are we editing an existing forecast?
      const currentlyEditedIndex = clonedForecasts.findIndex(f => f.id === draft.id);
      enrichedDraft = { ...enrichForecast(draft, userCapacityEntities), context: draft.context };
      if (currentlyEditedIndex === -1) {
        clonedForecasts.push(enrichedDraft);
        clonedForecasts.sort(sortForecastList);
      } else {
        // update the existing forecast in place with our updated one
        clonedForecasts.splice(currentlyEditedIndex, 1, enrichedDraft);
      }
    }
    return { forecasts: clonedForecasts, draft: enrichedDraft };
  }
);

interface ForecastHourMetaData {
  [forecastId: string]: {
    hourId: number;
    isTimerRunning: boolean;
  };
}

/**
 * Find "Forecasted time entries" on "today", for usage on Task lists
 * to quickly start/stop/resume a time entry's timer.
 */
export const getForecastedHoursForToday = createSelector(
  [getCalendarUserId, getHoursAsCards],
  (userId, hours): ForecastHourMetaData => {
    const today = DateTime.now().toISODate();

    return hours.reduce((map, hour) => {
      const belongsToCalendarUser = hour.user_id === userId;
      const isToday = hour.day === today;
      const hasForecastId = typeof hour.forecastId === 'number';
      const isUnlocked = !hour.isLocked;

      // Only map hours that are unlocked, have a forecast_id, belong to "today", for the current calendar user.
      if (!isUnlocked || !hasForecastId || !isToday || !belongsToCalendarUser) return map;

      // Try to only care about one time entry, even if multiple hour.forecast_id links exist -- adjust if it feels off.
      map[hour.forecastId!] = {
        hourId: hour.id,
        isTimerRunning: hour.isTimerRunning,
      };

      return map;
    }, {} as ForecastHourMetaData);
  }
);

export const getEnrichedForecastsForActingCalendarUser = createSelector(
  [getEnrichedForecastsWithDraft, getCalendarUserId],
  ({ forecasts }, calendarUserId) => {
    return forecasts.filter(forecast =>
      filterForecastListForUser(forecast, { userId: calendarUserId })
    );
  }
);

export const getForecastCalendarFilter = createSelector(
  [getCalendarUserId, getDates, getCalendarFilter],
  (calendarUserId, dates, calendarFilter) => {
    const since = dates[0];
    const until = dates[dates.length - 1];
    return {
      since,
      until,
      user_ids: [calendarUserId],
      span: calendarFilter.span,
    };
  }
);

export const getFilteredForecasts = createSelector(
  [getEnrichedForecastsWithDraft, getForecastQuery, getTeams],
  ({ forecasts }, query, teams) => {
    return filterForecastsByQuery({ query, forecasts, teams });
  }
);

// ---------------------------------------------------------------------------
// Analytics: Date Range Filtering
// ---------------------------------------------------------------------------
// The Report tab supports filtering by a date range. When a filter is active,
// statistics are scoped to that interval so users can understand how tasks are
// performing within a specific period.
//
// Estimated vs Logged — key design decision:
//
//   Metric     | Prorated?   | Rationale
//   -----------|-------------|--------------------------------------------------
//   Estimated  | Yes         | The estimate is a forecast — prorating it to the
//              |             | visible period shows the budget attributable to
//              |             | that window.
//   Logged     | No (actual) | Logged time is factual data tied to specific
//              |             | dates. When a filter is active, hour entries are
//              |             | fetched for the range and summed by forecast_id,
//              |             | replacing forecast.logged_duration (always total).
//   Remaining  | Derived     | prorated_estimate − logged
//   % Used     | Derived     | logged / prorated_estimate × 100
//
// User-facing examples:
//   • Task spans all of March, 20h estimate, 10h logged in March.
//     Filter on March → Estimated: 20h, Logged: 10h, % Used: 50%.
//
//   • Task spans March+April (61 days), 20h estimate, 10h logged in March.
//     Filter on March → Estimated: ~10.2h (prorated), Logged: 10h.
//     Filter on April → Estimated: ~9.8h (prorated), Logged: 0h, % Used: 0%.
//
// Architecture:
//   getDateRangeProrationFactor          — overlap fraction between task and filter dates
//   computeForecastAnalytics             — computes the four analytics fields for one forecast
//   computeLoggedMinutesByForecast       — aggregates hour entries into a map by forecast_id
//   getLoggedMinutesByForecastForDateRange — Redux selector; returns null when no filter active
//   getAnalyticsForecasts                — top-level memoized selector for the analytics route
//
// ForecastAnalyticsRoute fetches actual hour entries via fetchHours whenever the
// date filter changes. This is necessary because forecast.logged_duration from the
// API is always the task-level total and is not scoped by a date range.
// ---------------------------------------------------------------------------

// Analytics selectors
export interface AnalyticsForecast extends EnrichedForecast {
  analytics: {
    /** Estimated minutes prorated to the active date range filter */
    estimated_minutes: number;
    /** Actual logged minutes (not prorated — factual data tied to dates) */
    time_spent_minutes: number;
    remaining_minutes: number;
    percent_used: number;
  };
}

/**
 * Returns the fraction of a task's total duration that overlaps with the given
 * date range filter. Used to prorate the estimate to the visible period.
 *
 * Examples:
 *  - Task spans all of March, filter is all of March → 1.0 (full overlap)
 *  - Task spans March+April (61 days), filter is March only (31 days) → ~0.508
 *  - Task ends before filter starts → 0 (no overlap)
 *
 * @visibleForTesting
 */
export function getDateRangeProrationFactor(
  taskFrom: string,
  taskTo: string,
  filterSince: string,
  filterUntil: string
): number {
  const taskStart = DateTime.fromISO(taskFrom);
  const taskEnd = DateTime.fromISO(taskTo);
  const totalDays = taskEnd.diff(taskStart, 'days').days + 1;
  if (totalDays <= 0) return 1;

  const filterStart = DateTime.fromISO(filterSince);
  const filterEnd = DateTime.fromISO(filterUntil);

  const overlapStart = taskStart > filterStart ? taskStart : filterStart;
  const overlapEnd = taskEnd < filterEnd ? taskEnd : filterEnd;
  const overlapDays = overlapEnd.diff(overlapStart, 'days').days + 1;
  if (overlapDays <= 0) return 0;

  return Math.min(1, overlapDays / totalDays);
}

/**
 * Computes analytics for a single forecast given an optional date range filter.
 *
 * - Estimated minutes are prorated to the visible date range (forecast-level budget allocation).
 * - Logged time (time_spent_minutes) is NEVER prorated — it represents actual hours recorded
 *   on specific dates and is already scoped by the API's since/until parameters.
 * - Remaining and percent_used are derived from the (possibly prorated) estimate and actual logged time.
 *
 * @visibleForTesting
 */
export function computeForecastAnalytics(
  forecast: { from: string; to: string; estimated_minutes: number; logged_total_minutes: number },
  dateFilter?: { since: string; until: string }
): AnalyticsForecast['analytics'] {
  let estimatedMinutes = forecast.estimated_minutes;
  const timeSpentMinutes = forecast.logged_total_minutes;

  if (dateFilter) {
    const factor = getDateRangeProrationFactor(
      forecast.from,
      forecast.to,
      dateFilter.since,
      dateFilter.until
    );
    estimatedMinutes = Math.round(forecast.estimated_minutes * factor);
  }

  return {
    estimated_minutes: estimatedMinutes,
    time_spent_minutes: timeSpentMinutes,
    remaining_minutes: estimatedMinutes - timeSpentMinutes,
    percent_used: estimatedMinutes > 0 ? (timeSpentMinutes / estimatedMinutes) * 100 : 0,
  };
}

/**
 * Builds a map of forecast_id → total logged minutes from hour entries within [since, until].
 * Hours without a forecast_id or outside the date range are excluded.
 *
 * @visibleForTesting
 */
export function computeLoggedMinutesByForecast(
  hours: Array<{ forecast_id: number | null; day: string; duration: { total_minutes: number } }>,
  since: string,
  until: string
): Record<number, number> {
  const map: Record<number, number> = {};
  for (const hour of hours) {
    if (hour.forecast_id != null && hour.day >= since && hour.day <= until) {
      map[hour.forecast_id] = (map[hour.forecast_id] ?? 0) + hour.duration.total_minutes;
    }
  }
  return map;
}

/**
 * When a date range filter is active AND the corresponding hours have been fetched,
 * builds a map of forecast_id → total logged minutes from actual hour entries within the
 * date range. This gives us date-accurate logged time instead of the forecast's total
 * logged_duration which is NOT scoped by the API's date filter.
 *
 * Returns null when hours haven't been fetched yet or no date filter is active
 * (callers should fall back to forecast.logged_duration).
 */
const getLoggedMinutesByForecastForDateRange = createSelector(
  [getAllHours, getForecastQuery, getHoursXhrStatus],
  (hours, query, hoursXhrStatus): Record<number, number> | null => {
    if (!query.since || !query.until) return null;

    // Only compute from Redux hours once the analytics fetch has completed.
    // Before that, return null so callers fall back to forecast.logged_duration.
    const analyticsKey = `analytics-hours-${query.since}-${query.until}`;
    if (!xhrStatusUIState(hoursXhrStatus, analyticsKey).hasInitialized) return null;

    return computeLoggedMinutesByForecast(hours, query.since, query.until);
  }
);

export const getAnalyticsForecasts = createSelector(
  [getFilteredForecasts, getForecastQuery, getLoggedMinutesByForecastForDateRange],
  (forecasts, query, loggedByForecast): Array<AnalyticsForecast> => {
    const dateFilter =
      query.since && query.until ? { since: query.since, until: query.until } : undefined;

    return forecasts.map(forecast => {
      // When date filter is active, use actual hours within the range.
      // Otherwise, fall back to the forecast's total logged_duration.
      const loggedMinutes =
        loggedByForecast != null
          ? (loggedByForecast[forecast.id] ?? 0)
          : forecast.logged_duration.total_minutes;

      return {
        ...forecast,
        analytics: computeForecastAnalytics(
          {
            from: forecast.from,
            to: forecast.to,
            estimated_minutes: forecast.estimated_minutes,
            logged_total_minutes: loggedMinutes,
          },
          dateFilter
        ),
      };
    });
  }
);

// ---------------------------------------------------------------------------
// Unassigned time — hours logged in the date range that have no forecast_id.
// Returned pre-grouped by project, user, and client so the analytics view can
// inject virtual "Unassigned time" rows without recomputing inside components.
// ---------------------------------------------------------------------------

export interface UnassignedTimeGroup {
  key: string;
  name: string;
  meta?: { avatar?: string; color?: string };
  minutes: number;
}

export interface UnassignedTimeData {
  /** Total minutes of hours with no forecast_id in the date range */
  total_minutes: number;
  by_project: Array<UnassignedTimeGroup>;
  by_user: Array<UnassignedTimeGroup>;
  by_client: Array<UnassignedTimeGroup>;
}

const EMPTY_UNASSIGNED: UnassignedTimeData = {
  total_minutes: 0,
  by_project: [],
  by_user: [],
  by_client: [],
};

export const getUnassignedTimeForAnalytics = createSelector(
  [getAllHours, getHourProjectEntities, getForecastQuery],
  (hours, projectEntities, query): UnassignedTimeData => {
    const since = query.since;
    const until = query.until;

    // Without a date range we can't know which hours are in scope — fetchHours
    // is only dispatched when both since and until are set, so the store may
    // only contain hours from the calendar view's current week.
    if (!since || !until) return EMPTY_UNASSIGNED;

    const unassigned = hours.filter(h => h.forecast_id == null && h.day >= since && h.day <= until);

    if (unassigned.length === 0) return EMPTY_UNASSIGNED;

    const total_minutes = unassigned.reduce((sum, h) => sum + h.duration.total_minutes, 0);

    // Group by project (h.project is a number — the project ID)
    const projectMap = new Map<string, UnassignedTimeGroup>();
    for (const h of unassigned) {
      const projectId = h.project;
      const project = projectId ? projectEntities[projectId] : undefined;
      const key = project ? `project-${project.id}` : 'no-project';
      const existing = projectMap.get(key);
      if (existing) {
        existing.minutes += h.duration.total_minutes;
      } else {
        projectMap.set(key, {
          key,
          name: project?.name ?? 'No Project',
          meta: { color: project ? getProjectColor(project) : undefined },
          minutes: h.duration.total_minutes,
        });
      }
    }

    // Group by user
    const userMap = new Map<string, UnassignedTimeGroup>();
    for (const h of unassigned) {
      const key = `person-${h.user.id}`;
      const existing = userMap.get(key);
      if (existing) {
        existing.minutes += h.duration.total_minutes;
      } else {
        userMap.set(key, {
          key,
          name: h.user.name,
          meta: { avatar: h.user.avatar?.medium_retina },
          minutes: h.duration.total_minutes,
        });
      }
    }

    // Group by client
    const clientMap = new Map<string, UnassignedTimeGroup>();
    for (const h of unassigned) {
      const projectId = h.project;
      const project = projectId ? projectEntities[projectId] : undefined;
      const clientId = project?.client?.id;
      const key = clientId ? `client-${clientId}` : 'no-client';
      const existing = clientMap.get(key);
      if (existing) {
        existing.minutes += h.duration.total_minutes;
      } else {
        clientMap.set(key, {
          key,
          name: project?.client?.name ?? 'No Client',
          minutes: h.duration.total_minutes,
        });
      }
    }

    return {
      total_minutes,
      by_project: Array.from(projectMap.values()),
      by_user: Array.from(userMap.values()),
      by_client: Array.from(clientMap.values()),
    };
  }
);

export const getBatchUpdateLoadingStatus = createSelector([getRawXhrStatus], xhrStatus => {
  const isBatchUpdating = xhrStatusUIState(xhrStatus, 'batch-update').isLoading;
  const isBatchDeleting = xhrStatusUIState(xhrStatus, 'batch-delete').isLoading;

  return {
    isBatchUpdating,
    isBatchDeleting,
  };
});

export const getForecastFetchKeys = createSelector(
  [getForecastCalendarFilter],
  (filter): Record<ForecastStorageKeys, string> => {
    const since = fromISO(filter.since).minus({ month: 1 }).toISODate();
    const base = `for-${filter.user_ids}${new Date(since).getMonth()}`;
    return {
      active: `${base}-active`,
      completed: `${base}-completed`,
      upcoming: `${base}-upcoming`,
    };
  }
);

export const getForecastsByState = createSelector(
  [getEnrichedForecastsForActingCalendarUser, getForecastCalendarFilter, getStartOfWeek],
  (forecasts, filter, startOfWeek) => {
    const cutoffDateForUpcomingTask = getLastDayOfWeek(filter.until, startOfWeek);

    const filteredForecasts: ForecastStorageStates = forecasts.reduce(
      (
        previous: ForecastStorageStates,
        currentForecast: EnrichedForecast
      ): ForecastStorageStates => {
        if (currentForecast.completed_at) {
          previous.completed.push(currentForecast);
        } else if (currentForecast.from > cutoffDateForUpcomingTask) {
          previous.upcoming.push(currentForecast);
        } else {
          previous.active.push(currentForecast);
        }
        return previous;
      },
      { active: [], upcoming: [], completed: [] }
    );
    return filteredForecasts;
  }
);

export const getForecastListProps = createSelector(
  [
    getEnrichedForecastsForActingCalendarUser,
    getForecastsByState,
    getCalendarUserId,
    getUserId,
    getUserEntities,
    getForecastCalendarFilter,
    getRawXhrStatus,
    getForecastFetchKeys,
    getForecastSelectionsAsSet,
    getAccountFeatures,
    getUserProperties,
    getUserPermissions,
  ],
  (
    forecasts,
    { active, upcoming, completed },
    calendarUserId,
    authenticatedUserId,
    userMap,
    filter,
    xhrStatus,
    keys,
    selections,
    features,
    userProperties,
    permissions
  ) => {
    const isCompletedLoading = xhrStatusUIState(xhrStatus, keys.completed).isLoading;
    const isActiveLoading = xhrStatusUIState(xhrStatus, keys.active).isLoading;

    return {
      activeForecasts: active,
      upcomingForecasts: upcoming,
      completedForecasts: completed.sort(
        (a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime()
      ),
      forecastIds: forecasts.map(f => f.id),
      selections,
      summary: calculateForecastTotalsForAssignee(calendarUserId, forecasts),
      calendarUserId,
      activeUser: userMap[calendarUserId],
      filter,
      completedFetchKey: keys.completed,
      activeForecastFetchKey: keys.active,
      ui: {
        canLogTimeFromTask:
          authenticatedUserId === calendarUserId || !!permissions?.canUpdate?.projects,
        isActingAsSelf: authenticatedUserId === calendarUserId,
        hasCompletedForecasts: !!completed.length,
        isCompletedLoading,
        isActiveLoading,
        isForecastFeatureEnabled: !!features.tasks,
        totalForecastCount: active.length + upcoming.length,
        totalCompletedForecastCount: completed.length,
        hideTaskSidebar: !!userProperties[HIDE_TASK_SIDEBAR],
        canCreate: permissions.canCreate.forecasts,
        canUpdate: permissions.canUpdate.forecasts,
      },
    };
  }
);

export interface ProjectAndAssigneeForecastGroup {
  project: ProjectBase;
  project_summary: ForecastsSummary;
  forecasts: Array<EnrichedForecast>;
}

export const getForecastByProject = createSelector([getFilteredForecasts], forecasts => {
  const projects = forecasts.reduce<Array<ProjectAndAssigneeForecastGroup>>((prev, forecast) => {
    if (forecast.project?.id && !prev.find(p => p.project.id === forecast.project?.id)) {
      // All projects on plan
      const forecastsOnProject = forecasts.filter(f => f.project?.id === forecast.project?.id);
      prev.push({
        project: forecast.project,
        project_summary: calculateForecastTotalsForProject(forecastsOnProject),
        forecasts: forecastsOnProject,
      });
    }
    return prev;
  }, []);
  return sortBy(projects, 'project.name');
});

export const getFullProjectForForecastDraft = createSelector(
  [readForecastDraft, getProjectVariantMap],
  (draft, projects) => {
    if (draft?.project?.id) {
      const variant = projects[draft.project.id];
      if (variant?.type === 'full') {
        return variant.project;
      }
      return null;
    }
    return null;
  }
);

export const getRequiredTagDetailsForForecastDraft = createSelector(
  [getFullProjectForForecastDraft],
  project => {
    const labels = project?.labels ?? EMPTY_ARRAY;
    return {
      requiredTagIds: labels.filter(label => label.required).map(label => label.label_id),
      requiredLabels: !!project?.required_labels,
    };
  }
);

export const getEnrichedForecastDraft = createSelector(
  [getEnrichedForecastsWithDraft],
  ({ draft }) => {
    if (!draft) return null;
    return draft;
  }
);

export const getEnrichedForecastDraftOrThrow = createSelector([getEnrichedForecastDraft], draft => {
  if (!draft) throw Error('No forecast draft');
  return draft;
});

export const areWorkingDaysTheSameForAllForecastDraftUsers = createSelector(
  [getEnrichedForecastDraftOrThrow, getUserCapacitiesEntities],
  (draft, capacitiesByUserId) => {
    if (!draft.users) {
      return false;
    }
    if (draft.users.length <= 1) {
      return true;
    }
    const capacitiesPerUserByDay: Array<Array<{ date: DateTime; capacity: Capacity | undefined }>> =
      [];
    draft.users.forEach(user => {
      capacitiesPerUserByDay.push(
        getRelevantCapacitiesForAUser(draft.from, draft.to, capacitiesByUserId[user.id])
      );
    });

    // check to make sure that all the users have the same working days pattern
    const firstUserCapacities = capacitiesPerUserByDay[0];
    for (let i = 1; i < draft.users.length; i++) {
      const capacities = capacitiesPerUserByDay[i];
      for (let j = 0; j < capacities.length; j++) {
        const c = capacities[j];
        const matchingFirstUserCapacity = firstUserCapacities.find(c1 => c1.date.equals(c.date));
        if (!matchingFirstUserCapacity) {
          return false;
        }
        const dayNameShort = matchingFirstUserCapacity.date.toFormat('ccc').toUpperCase();
        if (
          c.capacity?.work_days_string.includes(dayNameShort) !==
          matchingFirstUserCapacity?.capacity?.work_days_string.includes(dayNameShort)
        ) {
          return false;
        }
      }
    }
    return true;
  }
);

export const getDailyEstimate = createSelector(
  [
    getEnrichedForecastDraftOrThrow,
    areWorkingDaysTheSameForAllForecastDraftUsers,
    getUserCapacitiesEntities,
  ],
  (draft, areWorkingDaysTheSame, capacitiesByUserId) => {
    if (
      !capacitiesByUserId ||
      typeof areWorkingDaysTheSame === 'undefined' ||
      draft.users.length === 0
    ) {
      return 0;
    }

    if (!areWorkingDaysTheSame) {
      return NaN;
    }

    const durationInWorkingDays = getNumberOfWorkingDaysForAUser(
      draft.from,
      draft.to,
      capacitiesByUserId[draft.users[0].id]
    );

    return durationInWorkingDays > 0
      ? (draft.estimated_minutes ?? 0) / 60 / (durationInWorkingDays * draft.users.length)
      : NaN;
  }
);

export const getTotalAvailableCapacityForForecastDraft = createSelector(
  [getEnrichedForecastDraftOrThrow, getEnrichedForecasts, getAllHours],
  (draft, tasks, hours) => {
    const { allocationByUser } = draft;

    // Start by summing total capacities of all assignees involved for that range
    let totalAvailableCapacityInMinutes =
      Object.values(allocationByUser).reduce(
        (total, user) => total + (user.totalCapacityForRange ?? 0),
        0
      ) * 60;

    // Find tasks of assignees that overlap with the draft task
    for (const userId in allocationByUser) {
      const filterOptions = {
        userId: Number(userId),
        from: draft.from,
        to: draft.to,
        excludeCompletedForecasts: true,
        hasOverlap: true,
      };
      const assigneeTasks = tasks.filter(
        t => filterForecastListForUser(t, filterOptions) && t.id !== draft.id
      );

      const assignee = draft.users.find(u => u.id === Number(userId));
      // Subtract all the assignee tasks (that overlap with the draft task date range) from the totalAvailableCapacityInMinutes
      assigneeTasks.forEach(task => {
        if (!task.completed_at) {
          const maxStartDate = fromISO(draft.from > task.from ? draft.from : task.from);
          const minEndDate = fromISO(draft.to < task.to ? draft.to : task.to);
          const overlapInDays =
            maxStartDate <= minEndDate ? getNumberOfDaysInRange(maxStartDate, minEndDate) : 0;
          for (let i = 0; i < overlapInDays; i++) {
            const overlapDay = maxStartDate.plus({ days: i });
            const isWorkingDay = assignee?.work_days?.includes(
              overlapDay.weekdayShort.toUpperCase()
            );
            if (isWorkingDay) {
              totalAvailableCapacityInMinutes -=
                task.allocationByUser[userId].dailyWorkMinutesRequiredToCompleteWithinRange;
            }
          }
        }
      });
    }

    // Subtract logged and planned hours
    hours.forEach(hour => {
      const {
        user: { id: userId },
      } = hour;
      if (
        draft.users.some(u => u.id === userId) &&
        hour.day >= draft.from &&
        hour.day <= draft.to
      ) {
        totalAvailableCapacityInMinutes -=
          hour.duration.total_minutes + hour.estimated_duration.total_minutes;
      }
    });

    // Subtract task estimate
    return (totalAvailableCapacityInMinutes - draft.estimated_minutes) / 60;
  }
);

export const getIsForecastMissingRequiredTags = createSelector(
  [getEnrichedForecastDraft, getRequiredTagDetailsForForecastDraft],
  (draft, { requiredTagIds, requiredLabels }) => {
    if (!requiredTagIds.length && !requiredLabels) {
      return false;
    }
    // Find the ids of required tags in use:
    const requiredTagsUsed = intersection(draft?.label_ids ?? [], requiredTagIds);

    return (
      (requiredLabels && !draft?.label_ids.length) ||
      requiredTagsUsed.length !== requiredTagIds.length
    );
  }
);

export const getForecastEditorUI = createSelector(
  [
    getEnrichedForecastDraft,
    getForecastXhrUiState,
    getIsForecastMissingRequiredTags,
    getUserPermissions,
  ],
  (forecastDraft, xhrState, isMissingTags, permissions) => {
    const isMissingTitle = !forecastDraft?.title.trim().length;
    const isMissingProject = !forecastDraft?.project?.id;
    const isMissingEstimate = !forecastDraft?.estimated_minutes;
    const isImpossibleEstimate =
      !!forecastDraft &&
      Object.entries(forecastDraft?.allocationByUser ?? {}).some(
        ([_, allocation]) => allocation.dailyUtilizationRate >= 150
      );

    const userNotAddedToProject = forecastDraft?.userLacksProjectPermission === true;
    const canEditUsers = permissions.canUpdate.users;
    const userNotAddedToProjectAndCantRectify = userNotAddedToProject && !canEditUsers;

    const { isSaving } = xhrState;
    const canUpdate = permissions.canUpdate.forecasts;
    const canDelete = permissions.canDelete.forecasts;

    const isSaveDisabled =
      isMissingTitle ||
      isMissingProject ||
      isMissingEstimate ||
      isMissingTags ||
      userNotAddedToProjectAndCantRectify ||
      isSaving ||
      !canUpdate;

    return {
      isSaving,
      isMissingTitle,
      isMissingProject,
      isMissingEstimate,
      isImpossibleEstimate,
      isSaveDisabled,
      isMissingTags,
      canUpdate,
      canDelete,
      isCreatingNew: forecastDraft?.temporaryId != null,
    };
  }
);

export const getForecastsByAssignee = createSelector([getFilteredForecasts], forecasts => {
  return orderBy(
    generateAssigneeGroupsFromForecasts(forecasts),
    [group => (group.assignee == null ? -1 : 0), group => group.assignee?.name],
    ['asc', 'asc']
  );
});

export const getAnalyticsForecastsByAssignee = createSelector(
  [getAnalyticsForecasts],
  forecasts => {
    return orderBy(
      generateAssigneeGroupsFromForecasts(forecasts),
      [group => (group.assignee == null ? -1 : 0), group => group.assignee?.name],
      ['asc', 'asc']
    );
  }
);

export const getAnalyticsForecastsByProject = createSelector([getAnalyticsForecasts], forecasts => {
  // Use Map for O(1) lookups instead of O(n) array.find()
  const projectsMap = new Map<number, ProjectAndAssigneeForecastGroup>();

  forecasts.forEach(forecast => {
    if (forecast.project?.id) {
      const projectId = forecast.project.id;
      const existing = projectsMap.get(projectId);

      if (existing) {
        existing.forecasts.push(forecast);
      } else {
        projectsMap.set(projectId, {
          project: forecast.project,
          project_summary: {
            task_count: 0,
            total_estimated_minutes: 0,
            total_progressed_minutes: 0,
            total_percent: 0,
          },
          forecasts: [forecast],
        });
      }
    }
  });

  // Calculate totals and convert to array
  const projects = Array.from(projectsMap.values()).map(group => ({
    ...group,
    project_summary: calculateForecastTotalsForProject(group.forecasts),
  }));

  return sortBy(projects, 'project.name');
});

export const getAnalyticsTotals = createSelector([getAnalyticsForecasts], forecasts => {
  const totals = forecasts.reduce(
    (acc, forecast) => {
      acc.minutesLogged += forecast.logged_duration.total_minutes;
      acc.minutesBilled += 0; // Forecasts don't have billed/unbilled distinction
      acc.minutesUnbilled += 0;
      acc.minutesPlanned += forecast.estimated_minutes;
      return acc;
    },
    {
      minutesLogged: 0,
      minutesPlanned: 0,
      minutesBilled: 0,
      minutesUnbilled: 0,
      costLogged: 0,
      costBilled: 0,
      costUnbilled: 0,
      costPlanned: 0,
    }
  );
  return totals;
});

const getForecastDraftUserId = createSelector(
  [getForecastDraft, getUserId],
  (draft, authenticatedUserId) =>
    draft?.users.length === 1 ? draft.users[0].id : authenticatedUserId
);

export const getRecentProjectsIdsForForecastDraft = createSelector(
  [getProjectPermissionsMap, getProjectVariantMap, getForecastDraftUserId],
  (permissionMap, variantMap, draftUserId) => {
    const permissions = permissionMap[draftUserId] ?? [];
    return projectSelectorFilterProjectIds(permissions, variantMap);
  }
);

export const getPinnedProjectsForForecastDraft = createSelector(
  [getRecentlyUsedPinnedProjectIdsSet, getProjectVariantMap, getAccountCurrency],
  (pinnedProjectIdsSet, projectById, currency): ProjectDropdownOption => {
    const pinnedProjects = Array.from(pinnedProjectIdsSet)
      .map(projectId => {
        return enrichProjectVariantOrNull(projectById[projectId], pinnedProjectIdsSet);
      })
      .filter(notNull);

    const children = pinnedProjects.map(project => ({
      value: project.base.id,
      label: project.base.name,
      color: project.base.color,
      billable: project.fullProject?.billable,
      client: project.base.client.name,
      clientId: project.base.client.id,
      budgetProgress: null,
      isArchived: !project.base.active,
      isPinned: project.isPinned,
      currency: currency.symbol,
      project: project.fullProject,
    }));

    return {
      value: -2,
      label: 'Pinned Projects',
      children,
      project: null,
    };
  }
);

export const getRecentProjectsForForecastDraft = createSelector(
  [
    getRecentProjectsIdsForForecastDraft,
    getProjectVariantMap,
    getRecentlyUsedPinnedProjectIdsSet,
    getAccountCurrency,
  ],
  (projectIds, projectById, recentlyUsedPinnedProjectIdsSet, currency): ProjectDropdownOption => {
    const recentProjects = projectIds
      .filter(projectId => !recentlyUsedPinnedProjectIdsSet.has(projectId))
      .map(projectId => {
        const project = enrichProjectVariantOrNull(
          projectById[projectId],
          recentlyUsedPinnedProjectIdsSet
        );
        if (project?.base.active) {
          return project;
        }
        return null;
      })
      .filter(notNull)
      .slice(0, 10);

    const children = recentProjects.map(project => {
      return {
        value: project.base.id,
        label: project.base.name,
        color: getProjectColor(project.base),
        billable: project.fullProject?.billable,
        client: project.base.client.name,
        clientId: project.base.client.id,
        budgetProgress: null,
        isArchived: !project.base.active,
        isPinned: project.isPinned,
        currency: currency.symbol,
        project: project.fullProject,
      };
    });

    return {
      value: -2,
      label: 'Recent Projects',
      children,
      project: null,
    };
  }
);

export const getFilteredForecastUsers = createSelector(
  [getNonDeletedUsers, getForecastQuery, getAllLoadedProjects],
  (users, query, projects) => {
    if (!query.onlyShowProjectAssignees || !query.projectIds.length || !projects) {
      return users;
    }
    const memberIds = new Set<number>();
    query.projectIds.forEach(projectId => {
      const project = projects.find(p => p.base.id === projectId);
      if (project?.fullProject?.users) {
        project.fullProject.users.forEach(user => {
          if (!user.deleted) {
            memberIds.add(user.user_id);
          }
        });
      }
    });

    return users.filter(user => memberIds.has(user.id));
  }
);

export const getForecastListHeader = createSelector(
  [getAllLoadedProjects, getActiveClients, getFilteredForecastUsers, getTeams, getForecastQuery],
  (projects, clients, users, teams, query) => {
    const projectId = query.projectIds.length === 1 ? query.projectIds[0] : undefined;
    const project = projectId ? projects.find(p => p.base.id === projectId) : undefined;

    return {
      projects,
      project,
      clients,
      users,
      teams,
      query,
    };
  }
);

export const getForecastsViewType = createSelector(
  [getUserProperties],
  (userProperties): ForecastViewType => {
    return (
      parseEnumValue(userProperties[PREFERRED_FORECASTS_VIEW_TYPE], FORECAST_VIEW_TYPES) ??
      'timeline'
    );
  }
);
